// Reconstruct what the research desk's open + closed book looked like on a
// given historical date. Pure function: walks the per-idea note arrays that
// research-notes.json already stores. No external state.
//
// Lets the UI render any past day without a separate per-day snapshot file —
// every NoteEntry already carries the drivers + fit snapshot from the day it
// was written.

import type { Idea, ResearchNotesFile } from './state-machine';

export interface ReconstructedBook {
  open: Idea[];
  closed: Idea[];
  hasData: boolean;
}

export const EMPTY_BOOK: ReconstructedBook = { open: [], closed: [], hasData: false };

/**
 * Returns the book as it would have looked on `date`. An idea is "open at
 * date" if it had been initiated by then AND wasn't yet closed by then. The
 * note rendered is the most recent one on or before `date`.
 *
 * If a note exactly on `date` doesn't exist (quiet day, weekend, etc), the
 * idea is included with its most recent prior note — same as a real PM
 * looking at "what's on the desk on this date."
 */
export function reconstructAtDate(notes: ResearchNotesFile, date: string): ReconstructedBook {
  const all = [...(notes.open || []), ...(notes.closed || [])];
  const open: Idea[] = [];
  const closed: Idea[] = [];
  let any = false;

  for (const idea of all) {
    if (!idea.initiationDate || idea.initiationDate > date) continue; // not yet a thing

    // Most-recent note on or before date.
    const eligible = (idea.notes || []).filter(n => n.date <= date);
    if (eligible.length === 0) continue;
    const lastNote = eligible[eligible.length - 1];
    any = true;

    const wasClosedByDate = idea.closedDate != null && idea.closedDate <= date;

    // Trim notes to those visible on/before `date` — deliberately keep them
    // newest-last so consumers reading `idea.notes[length-1]` get the
    // historical note, not today's.
    const trimmed: Idea = {
      ...idea,
      notes: eligible,
    };

    if (wasClosedByDate) {
      closed.push(trimmed);
    } else {
      // Reset open-state fields back to "open" — even if today the idea is
      // closed, on `date` it was still open.
      open.push({
        ...trimmed,
        status: 'open',
        closedDate: null,
        daysHeld: null,
        conviction: lastNote.conviction,
      });
    }
  }

  // Sort closed newest-first (matches today's view).
  closed.sort((a, b) => (b.closedDate || '').localeCompare(a.closedDate || ''));

  return { open, closed, hasData: any };
}

/**
 * The set of dates for which at least one note exists across all ideas
 * (open + closed). Useful for highlighting "days the desk published."
 * Returns sorted oldest→newest.
 */
export function notedDates(notes: ResearchNotesFile): string[] {
  const set = new Set<string>();
  for (const idea of [...(notes.open || []), ...(notes.closed || [])]) {
    for (const n of idea.notes || []) set.add(n.date);
  }
  return Array.from(set).sort();
}
